/**
 *
 * ModalLogin
 *
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { showTermsOfUse, showPrivacyPolicy } from 'containers/Main/actions';
import { setAuthModalAction } from 'containers/Auth/actions';
import { makeSelectStoredItem } from 'containers/Auth/selectors';
import {
  makeSelectOAuthOptions,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import ButtonBase from '@material-ui/core/ButtonBase';
import PageIntroBlock from 'components/PageIntroBlock';
import PasswordTextField from 'components/PasswordTextField';
import ProfileTextField from 'components/ProfileTextField';
import SAMLButton from 'components/SAMLButton';
import FacebookButton from 'components/SocialButtons/FacebookButton';
import GoogleButton from 'components/SocialButtons/GoogleButton';
import MicrosoftButton from 'components/SocialButtons/MicrosoftButton';
import useSAMLAuthentication from 'components/Hooks/useSAMLAuthentication';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Mixpanel from 'utils/mixpanelService';
import { emailRegex } from 'utils/stringUtils';
import SaveAssessmentCard from './SaveAssessmentCard';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '16px 32px',
    backgroundColor: 'white',
    maxHeight: '90%',
    overflowY: 'auto',
    position: 'absolute',
    top: props => props.top,
    [theme.breakpoints.up('md')]: {
      left: '50%',
      width: 500,
      marginLeft: -250,
    },
    [theme.breakpoints.down('sm')]: {
      width: '90%',
      left: 0,
      right: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  headerText: {
    paddingTop: 6,
  },
  closeIcon: {
    position: 'absolute',
    right: 12,
    top: 12,
    padding: 6,
  },
  caption: {
    ...theme.typography.overline,
    color: theme.palette.text.primary,
  },
  captionLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:focus': {
      outlineWidth: 0,
    },
    ...theme.typography.overline,
  },
  socialLoginContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 20,
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
  },
  dividerItem: {
    flex: 1,
    height: 1,
    backgroundColor: '#CECECE',
  },
  dividerText: {
    paddingLeft: 8,
    paddingRight: 8,
    width: '50%',
    textAlign: 'center',
    ...theme.typography.pxSmall,
    fontSize: '0.875rem',
  },
  formContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  button: {
    background: theme.palette.text.secondary,
    color: '#ffffff',
    width: '100%',
    padding: 12,
    // marginTop: 16,
    textTransform: 'uppercase',
    marginBottom: 5,
    '&:hover': {
      opacity: 0.8,
    },
    ...theme.typography.pSmallBold,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: 12,
  },
  signUpText: {
    fontSize: 16,
  },
  link: {
    marginLeft: 5,
    textDecoration: 'none',
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
}));

const ModalLogin = ({ onClose, onSignUp, type, positionTop }) => {
  const classes = useStyles({ top: positionTop });
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState([]);
  const authOptions = useSelector(makeSelectOAuthOptions());
  const storedItem = useSelector(makeSelectStoredItem());

  const [
    authModalSiteCopy,
    signInSiteCopy,
    signUpSiteCopy,
  ] = useSiteCopySelector(['authentication-modal', 'sign-in', 'sign-up']);
  const clientDetails = useSelector(makeSelectClientDetails());
  const hasClient = !_isEmpty(clientDetails);

  const handleSignUpClick = () => {
    Mixpanel.track('Authentication Modal - Switch to Sign Up', {
      trigger: storedItem.type,
    });
    dispatch(setAuthModalAction('sign-up'));
  };

  const handleCaptionClick = action => {
    if (action === 'agreement') dispatch(showTermsOfUse(true));
    if (action === 'privacy') dispatch(showPrivacyPolicy(true));
  };

  const handleSocialLogin = provider =>
    onSignUp({
      provider: { provider, type: 'popup' },
    });

  const handleFormChange = e => {
    if (e.target.name === 'password') {
      setPassword(e.target.value);
      if (error.includes('password')) {
        setError(error.filter(item => item !== 'password'));
      } else if (e.target.value === '') {
        setError(error.concat('password'));
      }
    } else if (e.target.name === 'email') {
      setEmail(e.target.value);
      if (error.includes('email')) {
        setError(error.filter(item => item !== 'email'));
      } else if (e.target.value === '') {
        setError(error.concat('email'));
      }
    }
  };

  const handleSubmit = e => {
    e.preventDefault();

    const formErrors = [];

    if (!emailRegex.test(email)) {
      formErrors.push('email');
    }
    if (password === '') {
      formErrors.push('password');
    }

    if (formErrors.length === 0) {
      onSignUp({ provider: { email, password } });
    } else {
      setError(formErrors);
    }
  };

  const action = `${_get(authModalSiteCopy, 'pageCopy.sign-in', '')} ${_get(
    authModalSiteCopy,
    ['pageCopy', type],
    '',
  )}`;

  const shouldDisplayGoogle = authOptions.google;
  const shouldDisplayFacebook = authOptions.facebook;
  const shouldDisplayMicrosoft = authOptions.ms;
  const shouldDisplaySAML = _get(
    clientDetails,
    'samlConfiguration.enabled',
    false,
  );
  const shouldDisplayOAUthOptions =
    shouldDisplayGoogle ||
    shouldDisplayFacebook ||
    shouldDisplayMicrosoft ||
    shouldDisplaySAML;

  const handleSAMLClick = useSAMLAuthentication({
    enableAutomatic: false,
  });

  return (
    <Grid container justifyContent="center" className={classes.container}>
      <IconButton
        className={classes.closeIcon}
        onClick={onClose}
        aria-label="Close"
      >
        <CloseIcon />
      </IconButton>
      <Grid item xs={12}>
        <Typography
          variant="h5"
          color="primary"
          className={classes.headerText}
          gutterBottom
        >
          {action}
        </Typography>
      </Grid>
      {type === 'move-assessments' && <SaveAssessmentCard />}
      <PageIntroBlock type="common" page="sign-in" modal />
      <Grid item xs={12}>
        {_get(signInSiteCopy, 'pageCopy.userTerms', []).map(el => {
          if (el.action) {
            return (
              <span
                key={el.value}
                role="link"
                tabIndex={0}
                onKeyPress={() => handleCaptionClick(el.action)}
                className={classes.captionLink}
                onClick={() => handleCaptionClick(el.action)}
              >
                {el.value}
              </span>
            );
          }

          return (
            <span key={el.value} className={classes.caption}>
              {el.value}
            </span>
          );
        })}
      </Grid>
      {shouldDisplayOAUthOptions && (
        <Grid item xs={12} className={classes.socialLoginContainer}>
          {shouldDisplayGoogle && (
            <GoogleButton
              onClick={() => handleSocialLogin('google')}
              text={_get(signInSiteCopy, 'pageCopy.googleLabel')}
            />
          )}
          {shouldDisplayFacebook && (
            <FacebookButton
              onClick={() => handleSocialLogin('facebook')}
              text={_get(signInSiteCopy, 'pageCopy.facebookLabel')}
            />
          )}
          {shouldDisplayMicrosoft && (
            <MicrosoftButton
              onClick={() => handleSocialLogin('microsoft.com')}
              text={_get(signInSiteCopy, 'pageCopy.microsoftLabel')}
            />
          )}
          <SAMLButton onClick={handleSAMLClick} />
        </Grid>
      )}
      {shouldDisplayOAUthOptions && authOptions.cm && hasClient && (
        <Grid item xs={12} className={classes.divider}>
          <div className={classes.dividerItem} />
          <Typography className={classes.dividerText} color="textPrimary">
            {_get(signInSiteCopy, 'pageCopy.emailModalCallToAction')}
          </Typography>
          <div className={classes.dividerItem} />
        </Grid>
      )}
      {authOptions.cm && (
        <>
          {hasClient && (
            <Grid item xs={12}>
              <form
                className={classes.formContainer}
                onSubmit={handleSubmit}
                noValidate
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ProfileTextField
                      field="email"
                      plain
                      value={email}
                      onChange={handleFormChange}
                      pattern="^\\S+@\\S+\\.\\S+$"
                      required
                      type="email"
                      error={error.includes('email')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <PasswordTextField
                      value={password}
                      onChange={handleFormChange}
                      plain
                      required
                      hideVisibilityToggle
                      error={
                        error.includes('password')
                          ? _get(signUpSiteCopy, 'pageCopy.passwordError')
                          : false
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ButtonBase className={classes.button} type="submit">
                      {_get(signInSiteCopy, 'pageCopy.cmLabel')}
                    </ButtonBase>
                  </Grid>
                </Grid>
              </form>
            </Grid>
          )}
          <Grid item xs={12} className={classes.divider}>
            <div className={classes.dividerItem} />
          </Grid>
          <Grid item xs={12} className={classes.footer}>
            <Typography className={classes.signUpText} color="textPrimary">
              {_get(signInSiteCopy, 'pageCopy.signUpModalLabel')}
              <span
                role="link"
                tabIndex={0}
                className={classes.link}
                onClick={handleSignUpClick}
              >
                {_get(signInSiteCopy, 'pageCopy.signUpModalLinkLabel')}
              </span>
            </Typography>
          </Grid>
        </>
      )}
    </Grid>
  );
};

ModalLogin.propTypes = {
  onClose: PropTypes.func,
  onSignUp: PropTypes.func,
  type: PropTypes.string,
  positionTop: PropTypes.string,
};

export default ModalLogin;
